import { cn } from '@lib/utils/helpers'

const DEFAULT_PER_PAGE: readonly number[] = [15, 30, 50, 100]

export interface ListPaginationControlsProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  /** e.g. `ca-list` → `data-testid="ca-list-pagination-header-first-button"` */
  testIdPrefix?: string
  placement: 'header' | 'footer'
  className?: string
}

/**
 * First / prev / numbered / next / last control cluster (matches Invitee list).
 */
export function ListPaginationControls({
  currentPage,
  totalPages,
  onPageChange,
  testIdPrefix = 'list',
  placement,
  className,
}: ListPaginationControlsProps) {
  const safeLast = Math.max(1, totalPages || 1)
  const current = Math.min(Math.max(1, currentPage || 1), safeLast)
  const tid = `${testIdPrefix}-pagination-${placement}`

  return (
    <div data-testid={tid} className={cn('flex items-center gap-1.5', className)}>
      <button
        data-testid={`${tid}-first-button`}
        type="button"
        className="flex h-8 min-w-[32px] items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all hover:bg-slate-50 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
        onClick={() => onPageChange(1)}
        disabled={current === 1}
        title="First"
      >
        &laquo;
      </button>
      <button
        data-testid={`${tid}-prev-button`}
        type="button"
        className="flex h-8 min-w-[32px] items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all hover:bg-slate-50 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
        onClick={() => onPageChange(current - 1)}
        disabled={current === 1}
        title="Previous"
      >
        &lsaquo;
      </button>

      {Array.from({ length: Math.min(5, safeLast) }, (_, i) => {
        let pageNum: number
        if (safeLast <= 5) {
          pageNum = i + 1
        } else if (current <= 3) {
          pageNum = i + 1
        } else if (current >= safeLast - 2) {
          pageNum = safeLast - 4 + i
        } else {
          pageNum = current - 2 + i
        }
        const isActive = current === pageNum
        return (
          <button
            key={pageNum}
            data-testid={`${tid}-page-${pageNum}-button`}
            type="button"
            className={cn(
              'flex h-8 min-w-[32px] items-center justify-center rounded-lg text-sm font-medium transition-all',
              isActive
                ? 'border border-button bg-button text-white shadow-md shadow-slate-300/30'
                : 'border border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50',
            )}
            onClick={() => onPageChange(pageNum)}
          >
            {pageNum}
          </button>
        )
      })}

      <button
        data-testid={`${tid}-next-button`}
        type="button"
        className="flex h-8 min-w-[32px] items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all hover:bg-slate-50 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
        onClick={() => onPageChange(current + 1)}
        disabled={current === safeLast}
        title="Next"
      >
        &rsaquo;
      </button>
      <button
        data-testid={`${tid}-last-button`}
        type="button"
        className="flex h-8 min-w-[32px] items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all hover:bg-slate-50 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
        onClick={() => onPageChange(safeLast)}
        disabled={current === safeLast}
        title="Last"
      >
        &raquo;
      </button>
    </div>
  )
}

export interface ListPaginationBarProps {
  placement: 'header' | 'footer'
  currentPage: number
  lastPage: number
  totalRecords: number
  perPage: number
  onPageChange: (page: number) => void
  onPerPageChange: (n: number) => void
  perPageOptions?: readonly number[]
  testIdPrefix?: string
  className?: string
  /** Optional `data-testid` on the bar container (e.g. invitee-list-pagination-header-bar). */
  barDataTestId?: string
  /** When false, hides the rows-per-page control (Invitee list shows it on the footer only). Default true. */
  showPerPageSelect?: boolean
  /**
   * `angular` — reports-style: `Page [n] of m (total Records)` with optional page jump when `lastPage > 1`.
   * Default Invitee-style: `Page n of m | Total x Records`.
   */
  summaryLayout?: 'default' | 'angular'
  /** When `summaryLayout="angular"` and `lastPage > 1`, show a small page-number input (Angular default payment logs). */
  showPageJump?: boolean
}

/**
 * "Go to page" number input (Angular `#gotoPage (change)="paginateItem(value)"`). Uncontrolled,
 * keyed on `current` so it resyncs when the page changes elsewhere; commits on Enter/blur and
 * clamps to [1, last]; non-numeric input resets to the current page (never emits out-of-range).
 */
function PageJumpInput({
  current,
  last,
  onPageChange,
  dataTestId,
}: {
  current: number
  last: number
  onPageChange: (page: number) => void
  dataTestId: string
}) {
  return (
    <input
      type="number"
      min={1}
      max={last}
      aria-label="Go to page"
      data-testid={dataTestId}
      className="w-14 rounded-md border border-slate-200 bg-white px-1.5 py-1 text-center text-sm font-bold text-slate-900 tabular-nums shadow-sm focus:border-[rgb(21_151_255)] focus:outline-none focus:ring-2 focus:ring-[rgb(21_151_255)]/20"
      defaultValue={current}
      key={`${dataTestId}-${current}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
      }}
      onBlur={(e) => {
        const v = parseInt(e.target.value, 10)
        if (Number.isNaN(v)) {
          e.target.value = String(current)
          return
        }
        const next = Math.min(last, Math.max(1, v))
        if (next !== current) onPageChange(next)
        e.target.value = String(next)
      }}
    />
  )
}

/**
 * Summary + pagination + rows-per-page (matches `InviteeList` result card bars).
 */
export function ListPaginationBar({
  placement,
  currentPage,
  lastPage,
  totalRecords,
  perPage,
  onPageChange,
  onPerPageChange,
  perPageOptions = DEFAULT_PER_PAGE,
  testIdPrefix = 'list',
  className,
  barDataTestId,
  showPerPageSelect = true,
  summaryLayout = 'default',
  showPageJump = true,
}: ListPaginationBarProps) {
  const edge =
    placement === 'header' ? 'border-b border-slate-100' : 'border-t border-slate-100'
  const summaryId = `${testIdPrefix}-pagination-${placement}-summary`
  const rowsLabelId = `${testIdPrefix}-per-page-label-${placement}`
  const safeLast = Math.max(1, lastPage || 1)
  const safeCurrent = Math.min(Math.max(1, currentPage || 1), safeLast)

  const summaryAngular =
    summaryLayout === 'angular' ? (
      <span className="inline-flex flex-wrap items-center justify-center gap-x-1 gap-y-1 sm:justify-start">
        {safeLast > 1 ? (
          <>
            <span>Page</span>
            {showPageJump ? (
              <PageJumpInput
                current={safeCurrent}
                last={safeLast}
                onPageChange={onPageChange}
                dataTestId={`${testIdPrefix}-pagination-${placement}-page-jump`}
              />
            ) : (
              <span className="font-bold text-slate-900">{safeCurrent}</span>
            )}
            <span>of {safeLast}</span>
          </>
        ) : null}
        {totalRecords > 0 ? (
          <span className={safeLast > 1 ? 'ml-1 sm:ml-2' : ''}>
            ({totalRecords} Records)
          </span>
        ) : null}
      </span>
    ) : null

  const summaryDefault =
    summaryLayout === 'default' ? (
      <span className="inline-flex flex-wrap items-center justify-center gap-x-1 gap-y-1 sm:justify-start">
        <span>Page</span>
        {showPageJump && safeLast > 1 ? (
          <PageJumpInput
            current={safeCurrent}
            last={safeLast}
            onPageChange={onPageChange}
            dataTestId={`${testIdPrefix}-pagination-${placement}-page-jump`}
          />
        ) : (
          <span className="font-bold text-slate-900">{safeCurrent}</span>
        )}
        <span>
          of <span className="font-bold text-slate-900">{safeLast}</span>
        </span>
        <span className="mx-1 text-slate-300">|</span>
        <span>
          Total <span className="font-bold text-slate-900">{totalRecords}</span> Records
        </span>
      </span>
    ) : null

  return (
    <div
      data-testid={barDataTestId}
      className={cn(
        'flex flex-col items-stretch justify-between gap-3 bg-slate-50/50 px-3 py-3 sm:gap-4 sm:px-6 sm:py-4',
        'sm:flex-row sm:items-center',
        edge,
        className,
      )}
    >
      <div data-testid={summaryId} className="text-center text-xs font-medium text-slate-500 sm:text-left sm:text-sm">
        {summaryLayout === 'angular' ? summaryAngular : summaryDefault}
      </div>

      <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-end">
        <ListPaginationControls
          currentPage={currentPage}
          totalPages={lastPage}
          onPageChange={onPageChange}
          testIdPrefix={testIdPrefix}
          placement={placement}
        />
        {showPerPageSelect ? (
          <div className="ml-0 flex items-center gap-2 sm:ml-4">
            <span
              data-testid={rowsLabelId}
              className="text-xs font-semibold uppercase tracking-wider text-slate-400"
            >
              Rows
            </span>
            <select
              data-testid={`${testIdPrefix}-per-page-select-${placement}`}
              className="block rounded-lg border border-slate-200 bg-white p-2 text-sm text-slate-700 focus:border-button focus:outline-none focus:ring-2 focus:ring-button/30"
              value={String(perPage)}
              onChange={(e) => onPerPageChange(Number(e.target.value))}
            >
              {perPageOptions.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        ) : null}
      </div>
    </div>
  )
}
